import { useEffect, useState, useMemo } from "react";
import SEO from "../../components/common/SEO";
import { generateBreadcrumbSchema } from "../../utils/seo/structuredData";
import GlobalActiveLineup from "../../components/global/GlobalActiveLineup";
import { useDispatch } from "react-redux";
import { useLeagueDetails } from "../../hooks/league/useLeagueDetails";
import { useLeagueMembers } from "../../hooks/league/useFetchLeagueMembers";
import Loading from "../../components/common/Loading";
import GlobalLeaderboard from "../../components/global/GlobalLeaderboard";
import { useLocation } from "react-router-dom";
import { setLeagueId } from "../../features/league/leagueReducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFire, faTrophy, faUsers } from "@fortawesome/free-solid-svg-icons";
import { GlobeIcon } from "@heroicons/react/outline";
import GlobalFightCard from "../../components/global/GlobalFightCard";
import { useEventsWithLeagueCount } from "../../hooks/events/useEventsWithLeagueCount";
import { useEventDetails } from "../../hooks/events/useEventDetails";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useFetchAllUserLineups } from "../../hooks/lineup/useFetchAllUserLineups";

const GlobalCompetition = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const globalCompetitionId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;
  const [activeTab, setActiveTab] = useState("lineup");

  const { events, isLoading: isEventsLoading } = useEventsWithLeagueCount();

  // Fetch global competition members to get total player count
  const { data: globalMembersData, isLoading: isGlobalMembersLoading } =
    useLeagueMembers(globalCompetitionId, 1, 1, "");

  const totalPlayers = globalMembersData?.total || 0;

  // Fetch current user data
  const { data: dbUser } = useFetchUser();

  // Fetch a page of members to find user's rank and points
  const { data: rankingData } = useLeagueMembers(
    globalCompetitionId,
    1,
    100,
    "",
  );

  // Calculate user's rank and points from the leaderboard data
  const userStats = useMemo(() => {
    if (!rankingData?.data || !dbUser?._id) {
      return { rank: null, points: null };
    }
    const userIndex = rankingData.data.findIndex(
      (member) => member.userId === dbUser._id,
    );
    if (userIndex !== -1) {
      return {
        rank: userIndex + 1,
        points: rankingData.data[userIndex].points || 0,
      };
    }
    return { rank: null, points: null };
  }, [rankingData, dbUser]);

  // Fetch user's global entries count
  const { data: userGlobalLineups } = useFetchAllUserLineups(
    globalCompetitionId,
    dbUser?._id,
  );
  const totalEntries = userGlobalLineups?.length || 0;

  // Get the next upcoming event for the Fights tab
  const nextEvent = useMemo(() => {
    if (!events || events.length === 0) return null;
    const now = new Date();
    // Find the closest upcoming event, or the most recent one if all are past
    const upcoming = events
      .filter((event) => new Date(event.DateTime) > now)
      .sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
    return upcoming[0] || null;
  }, [events]);

  const nextEventId = nextEvent?.EventId || nextEvent?._id || null;

  // Fetch fight card for the next event
  const { data: nextEventDetails, isLoading: isEventDetailsLoading } =
    useEventDetails(nextEventId);

  useEffect(() => {
    if (location.pathname === "/globalcompetition") {
      dispatch(setLeagueId(globalCompetitionId));
    }
  }, [location.pathname, globalCompetitionId, dispatch]);

  const { isLoading: isLeagueLoading, error: isLeagueError } =
    useLeagueDetails(globalCompetitionId);

  // Show fullscreen loading until critical data is ready
  const isInitialLoading =
    isLeagueLoading || isEventsLoading || isGlobalMembersLoading;

  if (isInitialLoading) return <Loading />;
  if (isLeagueError)
    return <div>Error fetching league details: {isLeagueError.message}</div>;

  const breadcrumbs = [
    { name: "Home", path: "/home" },
    { name: "Global Competition", path: "/globalcompetition" },
  ];

  const tabs = [
    { id: "lineup", label: "My Lineup", icon: faUsers },
    { id: "fights", label: "Fights", icon: faFire },
    { id: "leaderboard", label: "Leaderboard", icon: faTrophy },
  ];

  return (
    <div className="space-y-0 pb-6">
      <SEO
        title="Global Competition - Compete Worldwide | DayShift MMA Fantasy"
        description={`Join ${totalPlayers.toLocaleString()}+ players in the global UFC fantasy competition. Build your lineup, compete for rankings, and prove you're the best fantasy MMA player worldwide.`}
        keywords="UFC fantasy global competition, MMA fantasy leaderboard, worldwide UFC fantasy, fantasy MMA rankings, global fantasy league"
        url="/globalcompetition"
        structuredData={generateBreadcrumbSchema(breadcrumbs)}
      />

      {/* Compact Competition Header */}
      <div className="bg-gradient-to-br from-dark-grey via-gray-700 to-gray-800 rounded-xl p-4 text-white shadow-lg mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-deep-red flex items-center justify-center shadow-lg">
              <GlobeIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">
                Global Competition
              </h1>
              <p className="text-white/70 text-xs">Compete worldwide</p>
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs font-bold bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
              LIVE
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-white">{totalEntries}</div>
            <div className="text-white/60 text-[10px] uppercase tracking-wide">
              Entries
            </div>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-gold">
              {userStats.rank ? `#${userStats.rank}` : "—"}
            </div>
            <div className="text-white/60 text-[10px] uppercase tracking-wide">
              Your Rank
            </div>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-white">
              {userStats.points !== null ? userStats.points.toFixed(2) : "—"}
            </div>
            <div className="text-white/60 text-[10px] uppercase tracking-wide">
              Points
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Tab Navigation */}
      <div className="sticky top-[60px] z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 rounded-t-xl -mx-5 px-5 md:-mx-0 md:px-0 md:rounded-none">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? "text-deep-red"
                  : "text-medium-grey hover:text-dark-grey"
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
                {tab.label}
              </span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-deep-red rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="pt-4">
        {/* Fights Tab */}
        {activeTab === "fights" && (
          <div className="space-y-4">
            {/* Current Event Fight Card */}
            {nextEvent && (
              <div className="space-y-3">
                {/* Event Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-dark-grey to-gray-700 p-4">
                    <h2 className="text-base font-bold text-white">
                      {nextEvent.Name || nextEvent.ShortName}
                    </h2>
                    <p className="text-white/70 text-xs mt-0.5">
                      {new Date(nextEvent.DateTime).toLocaleDateString(
                        "en-US",
                        {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        },
                      )}
                    </p>
                  </div>
                </div>

                {/* Fight Cards */}
                {isEventDetailsLoading ? (
                  <FightCardSkeleton />
                ) : nextEventDetails?.Fights ? (
                  <GlobalFightCard
                    fights={nextEventDetails.Fights}
                    eventName={nextEvent.Name || nextEvent.ShortName}
                    eventDateTime={nextEvent.DateTime}
                    eventId={nextEventId}
                  />
                ) : (
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 text-center text-medium-grey text-sm">
                    Fight card not yet available
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && <GlobalLeaderboard />}

        {/* My Lineup Tab */}
        {activeTab === "lineup" && (
          <div className="space-y-4">
            <GlobalActiveLineup leagueId={globalCompetitionId} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Skeleton loader for fight cards
 */
const FightCardSkeleton = () => (
  <div className="space-y-3 animate-pulse">
    {Array.from({ length: 3 }).map((_, idx) => (
      <div
        key={idx}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
      >
        <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
          <div className="h-4 bg-gray-200 rounded w-24"></div>
          <div className="h-4 bg-gray-200 rounded w-16"></div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-16 mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-20"></div>
            </div>
            <div className="px-3">
              <div className="h-6 bg-gray-200 rounded w-8"></div>
            </div>
            <div className="flex-1 flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-16 mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-20"></div>
            </div>
          </div>
          <div className="mt-4 h-10 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    ))}
  </div>
);

export default GlobalCompetition;
